import React, { useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Bot,
  CalendarClock,
  Check,
  ChevronDown,
  FlaskConical,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import {
  type ActionEntity,
  type Experiment,
  type ExperimentEvaluation,
  type ExperimentMetricPlan,
} from "./types.ts";
import { entityDisplayLabel } from "./targeting.ts";

function dateTime(value?: string | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleString("zh-CN", { hour12: false });
}

export function resolveMarketplaceCurrency(marketplace?: string | null, scopeCurrency?: string | null): string {
  if (scopeCurrency) return scopeCurrency.toUpperCase();
  const normalized = String(marketplace ?? "").trim().toUpperCase();
  if (normalized === "UK" || normalized === "GB") return "GBP";
  if (normalized === "US" || normalized === "COM") return "USD";
  if (normalized === "JP") return "JPY";
  if (normalized === "CA") return "CAD";
  if (normalized === "AU") return "AUD";
  return "EUR";
}

export function ExperimentCard({
  item,
  isExpanded = false,
  onToggle,
}: {
  item: Experiment;
  isExpanded?: boolean;
  onToggle?: () => void;
}) {
  const [localExpanded, setLocalExpanded] = useState(isExpanded);
  const expanded = onToggle ? isExpanded : localExpanded;
  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setLocalExpanded((v) => !v);
    }
  };

  const evaluation = item.latest_evaluation;
  const state = item.state === "ROLLED_BACK" ? item.state : evaluation?.classification ?? item.state;
  const rollback = state !== "ROLLED_BACK" && (evaluation?.decision.needs_rollback ?? false);
  const beforeBid = (() => {
    const raw = (item.definition as any)?.before_value ?? (item.definition as any)?.rollback_value ?? item.definition.rollback?.value;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const bid = (raw as Record<string, unknown>).bid;
    const parsed = typeof bid === "number" ? bid : Number(bid);
    return Number.isFinite(parsed) ? parsed : null;
  })();
  const appliedBid = (() => {
    const raw = (item.definition as any)?.applied_value;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const bid = (raw as Record<string, unknown>).bid;
    const parsed = typeof bid === "number" ? bid : Number(bid);
    return Number.isFinite(parsed) ? parsed : null;
  })();
  const isBidReduction = beforeBid != null && appliedBid != null && appliedBid < beforeBid;
  const noAutomaticRollback = isBidReduction || !Array.isArray(item.definition.rollback?.recommended_for) || item.definition.rollback.recommended_for.length === 0;
  const experimentEntityLabel = entityDisplayLabel(item.entity, item.definition.entity_label || item.campaign_name || item.resource_type || "未解析投放");

  return (
    <article className={`experiment-card ${rollback ? "rollback" : ""} ${expanded ? "is-expanded" : "is-collapsed"}`}>
      <header
        className="experiment-header clickable"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-expanded={expanded}
      >
        <div className="experiment-header-left">
          <div className="experiment-heading-icon">
            <FlaskConical />
          </div>
          <div className="experiment-heading-main">
            <div className="experiment-eyebrow-line">
              <span>{item.marketplace} 站</span>
              <span className="dot">·</span>
              <span>SKU {item.sku}</span>
              <span className="dot">·</span>
              <span>{item.definition.campaign_role}</span>
            </div>
            <h2 className="experiment-title">
              {experimentEntityLabel}
            </h2>
            <p className="experiment-hypothesis">
              {formatExperimentHypothesis(item.definition.hypothesis, experimentEntityLabel)}
            </p>
          </div>
        </div>

        <div className="experiment-header-right">
          <span className={`status status-${state.toLowerCase().replaceAll("_", "-")}`}>
            {experimentDecisionTitle(state)}
          </span>
          <button
            type="button"
            className={`experiment-ghost-toggle ${expanded ? "expanded" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            aria-label={expanded ? "收起复盘详情" : "展开复盘详情"}
          >
            <span>{expanded ? "收起" : "展开"}</span>
            <ChevronDown size={14} className="toggle-chevron" />
          </button>
        </div>
      </header>

      {expanded && (
        <div className="experiment-body">
          <div className="experiment-timeline" aria-label="观察时间线">
            <ExperimentMilestone label="动作生效" value={dateTime(item.created_at)} active />
            <ExperimentMilestone
              label="安全复盘"
              value={`${dateTime(item.earliest_review_at)} (硬止损检查)`}
              active={Date.now() >= Date.parse(item.earliest_review_at)}
            />
            <ExperimentMilestone
              label="成熟复盘"
              value={`${dateTime(item.mature_review_at)} (效果定论)`}
              active={Date.now() >= Date.parse(item.mature_review_at)}
            />
            <span className="lag-note">
              <CalendarClock />
              已剔除最近 {item.definition.observation.excluded_reporting_lag_days} 天归因延迟
            </span>
          </div>

          <div className="experiment-content">
            <section className="experiment-metrics">
              <div className="experiment-section-title">
                <div>
                  <BarChart3 />
                  <strong>指标变化与阈值比对</strong>
                </div>
                <small>{evaluation?.metrics.data_window ?? "等待复盘数据切片"}</small>
              </div>
              <div className="experiment-metric-table">
                <div className="experiment-metric-row header">
                  <span>指标</span>
                  <span>基线</span>
                  <span>当前</span>
                  <span>变化率</span>
                  <span>有效判定标准</span>
                </div>
                {item.definition.metrics.map((metric) => (
                  <ExperimentMetricRow
                    key={metric.key}
                    metric={metric}
                    current={evaluation?.metrics.current[metric.key] ?? null}
                    delta={evaluation?.metrics.deltas[metric.key] ?? null}
                    currency={resolveMarketplaceCurrency(item.marketplace)}
                  />
                ))}
              </div>
              <div className="experiment-evidence-floor">
                最低置信样本量：{item.definition.evidence_thresholds.min_clicks} 次点击
                {item.definition.evidence_thresholds.min_orders > 0 ? `、${item.definition.evidence_thresholds.min_orders} 个订单` : ""}
              </div>
            </section>

            <aside className={`experiment-decision ${rollback ? "rollback" : ""}`}>
              <div className="experiment-section-title">
                <div>
                  {rollback ? <RotateCcw /> : <ShieldCheck />}
                  <strong>复盘结论</strong>
                </div>
              </div>
              <strong className="decision-title">{experimentDecisionTitle(state)}</strong>
              <p>
                {state === "ROLLED_BACK" ? "回滚动作已经审批、执行并通过实时读回验证，原实验已关闭。" : evaluation?.decision.summary ??
                  `观察 ${item.definition.observation.mature_review_days} 天后定论；未到期前不提前判定有效或无效。`}
              </p>
              <dl>
                <div>
                  <dt>是否有效</dt>
                  <dd>
                    {evaluation?.decision.effective == null
                      ? "待观察"
                      : evaluation.decision.effective
                      ? "有效"
                      : "无效"}
                  </dd>
                </div>
                <div>
                  <dt>是否回滚</dt>
                  <dd className={rollback ? "danger" : ""}>{state === "ROLLED_BACK" ? "已完成回滚" : rollback ? "建议审批回滚" : "暂不回滚"}</dd>
                </div>
                <div>
                  <dt>{noAutomaticRollback ? "原竞价参考值（不自动回滚）" : "预设回滚值"}</dt>
                  <dd className="rollback-value-dd">
                    {renderRollbackValue(item.definition.rollback.value)}
                  </dd>
                </div>
              </dl>
              <div className="next-ai-context">
                <Bot />
                <span>本实验及最新结论已纳入下一次 AI 优化输入上下文</span>
              </div>
            </aside>
          </div>

          <footer className="experiment-criteria">
            <div className="criteria-card success">
              <div className="criteria-card-header">
                <ShieldCheck className="criteria-icon" />
                <strong>有效判定标准</strong>
              </div>
              <div className="criteria-list">
                {item.definition.success_criteria.map((text) => (
                  <div key={text} className="criteria-item">
                    <Check className="item-icon" />
                    <span>{formatCriteriaText(text)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="criteria-card hard-stop">
              <div className="criteria-card-header">
                <AlertTriangle className="criteria-icon" />
                <strong>{noAutomaticRollback ? "提前干预触发条件" : "提前回滚触发条件"}</strong>
              </div>
              <div className="criteria-list">
                {item.definition.hard_stop_conditions.map((text) => (
                  <div key={text} className="criteria-item">
                    <AlertTriangle className="item-icon" />
                    <span>{formatCriteriaText(text)}</span>
                  </div>
                ))}
              </div>
            </div>
          </footer>
        </div>
      )}
    </article>
  );
}

export function formatCriteriaText(text: string): string {
  return text.replace(/(\d+)\.\d+(\s*单)/g, "$1$2").replace(/(\d+)\.\d+(\s*次)/g, "$1$2");
}

export function ExperimentMilestone({
  label,
  value,
  active,
}: {
  label: string;
  value: string;
  active: boolean;
}) {
  return (
    <span className={`experiment-milestone ${active ? "active" : ""}`}>
      <i />
      <small>{label}</small>
      <strong>{value}</strong>
    </span>
  );
}

export function compareMetric(operator?: string, current?: number | null, threshold?: number | null): boolean {
  if (current == null || threshold == null) return false;
  if (operator === "LTE") return current <= threshold;
  if (operator === "GTE") return current >= threshold;
  if (operator === "LT") return current < threshold;
  if (operator === "GT") return current > threshold;
  return false;
}

export function ExperimentMetricRow({
  metric,
  current,
  delta,
  currency,
}: {
  metric: ExperimentMetricPlan;
  current: number | null;
  delta: number | null;
  currency: string;
}) {
  const hasTarget = metric.success_value != null;
  const hasHardStop = metric.hard_stop_value != null;
  const isPending = current == null || (!hasTarget && !hasHardStop && metric.role !== "SECONDARY");
  let threshold = "待数据完整";
  if (hasTarget) {
    threshold = `${operatorLabel(metric.success_operator)} ${formatExperimentMetric(metric, metric.success_value, currency)}`;
  } else if (hasHardStop) {
    const safeOp = metric.hard_stop_operator === "GT" ? "≤" : metric.hard_stop_operator === "LT" ? "≥" : operatorLabel(metric.hard_stop_operator);
    threshold = `${safeOp} ${formatExperimentMetric(metric, metric.hard_stop_value, currency)} (安全护栏)`;
  } else if (metric.role === "SECONDARY") {
    threshold = "辅助参考";
  }

  const good =
    !isPending &&
    delta != null &&
    hasTarget &&
    ((metric.success_operator === "LTE" && delta <= 0) || (metric.success_operator === "GTE" && delta >= 0));
  const isHardStopBreached =
    hasHardStop &&
    current != null &&
    compareMetric(metric.hard_stop_operator, current, metric.hard_stop_value);
  const bad =
    (!isPending &&
      delta != null &&
      hasTarget &&
      ((metric.success_operator === "LTE" && delta > 0) || (metric.success_operator === "GTE" && delta < 0))) ||
    isHardStopBreached;

  return (
    <div className={`experiment-metric-row ${isPending ? "is-pending" : ""}`}>
      <span className="metric-col-name">
        <b>{metric.label}</b>
        <small className={`role-badge role-${metric.role.toLowerCase()}`}>{metricRoleLabel(metric.role)}</small>
      </span>
      <span className="metric-col-baseline">{formatExperimentMetric(metric, metric.baseline, currency)}</span>
      <span className={`metric-col-current ${isPending ? "muted" : ""}`}>
        {formatExperimentMetric(metric, current, currency)}
      </span>
      <span className={`metric-col-delta ${isPending || delta == null ? "muted" : good ? "good" : bad ? "bad" : "muted"}`}>
        {delta == null ? "-" : `${delta > 0 ? "+" : ""}${(delta * 100).toFixed(1)}%`}
      </span>
      <span className={`metric-col-threshold ${isPending ? "muted" : ""}`}>{threshold}</span>
    </div>
  );
}

export function renderRollbackValue(value: unknown) {
  if (value == null) return <span className="muted">-</span>;
  if (typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="muted">-</span>;
    return (
      <span className="rollback-tag-group">
        {entries.map(([k, v]) => (
          <span key={k} className="rollback-param-badge">
            <span className="param-key">{k}</span>
            <span className="param-sep">:</span>
            <span className="param-val">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
          </span>
        ))}
      </span>
    );
  }
  return <span className="rollback-param-badge">{String(value)}</span>;
}

export function formatExperimentMetric(metric: ExperimentMetricPlan, value: number | null | undefined, currency: string): string {
  if (value == null) return "-";
  if (metric.format === "PERCENT") return `${(value * 100).toFixed(2)}%`;
  if (metric.format === "CURRENCY")
    return new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(value);
  if (metric.format === "RATIO") return value.toFixed(2);
  if (metric.key === "orders" || metric.key === "clicks" || metric.format === "NUMBER") {
    return Number.isInteger(value) ? String(value) : String(Math.round(value));
  }
  return value.toFixed(value % 1 === 0 ? 0 : 1);
}

export function formatExperimentHypothesis(hypothesis?: string | null, entityLabel?: string | null): string {
  if (!hypothesis) return "";
  let text = hypothesis;
  if (entityLabel && entityLabel.trim()) {
    const escaped = entityLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    text = text.replace(new RegExp(`^将\\s*${escaped}\\s*的实际(关键词|投放)?竞价`, "g"), "将该词的实际竞价");
    text = text.replace(new RegExp(`将\\s*${escaped}\\s*的实际(关键词|投放)?竞价`, "g"), "将该词的实际竞价");
    text = text.replace(new RegExp(`将\\s*${escaped}\\s*的`, "g"), "将该对象的");
  }
  text = text.replace(/^将\s*\[.*?\]\s*.*?\s*的实际(关键词|投放)?竞价/, "将该词的实际竞价");
  text = text.replace(/\{\s*"bid"\s*:\s*"([^"]+)"\s*\}/g, "$1").replace(/\{\s*"bid"\s*:\s*([0-9.]+)\s*\}/g, "$1");
  return text;
}

export function metricRoleLabel(role: ExperimentMetricPlan["role"]): string {
  return role === "PRIMARY" ? "主要指标" : role === "GUARDRAIL" ? "业务护栏" : "辅助指标";
}

export function operatorLabel(operator?: string): string {
  return operator === "GTE" ? "≥" : operator === "LTE" ? "≤" : operator === "GT" ? ">" : "<";
}

export function experimentDecisionTitle(state: string): string {
  const labels: Record<string, string> = {
    OBSERVING: "观察期中",
    EXTENDED: "延长观察",
    TOO_EARLY: "尚未到成熟期",
    INSUFFICIENT_DATA: "证据样本不足",
    IMPROVED: "优化有效",
    MIXED: "效果混合",
    INEFFECTIVE: "未见实质改善",
    DEGRADED: "指标恶化 (触发回滚)",
    ROLLED_BACK: "已回滚并验证",
    MANUAL_OVERRIDE: "检测到人工修改",
  };
  return labels[state] ?? state;
}
